import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useConfig } from "@/hooks/useConfig";
import { labelFor } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Plus, Eye } from "lucide-react";
import DPOrderRulesDialog from "./DPOrderRulesDialog";
import { AccessBlocked } from "@/components/AccessBlocked";

const STATUS_COLORS = {
  applied: "bg-blue-50 text-blue-700 border-blue-200",
  kyc_submitted: "bg-amber-50 text-amber-700 border-amber-200",
  under_review: "bg-violet-50 text-violet-700 border-violet-200",
  sent_back: "bg-orange-50 text-orange-700 border-orange-300",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-600 border-red-200",
};

export default function DPPipelinePage() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [blocked, setBlocked] = useState(null);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const { getValues } = useConfig();
  const statuses = getValues("dp_onboarding_status");

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.set("status", statusFilter);
    if (search) params.set("search", search);
    api.get(`/dp/partners?${params}`)
      .then(r => { setProfiles(r.data || []); setBlocked(null); })
      .catch(e => {
        // FX3 C11 — a 403 is an access state, not a toast on an empty table
        if ([401, 403, 410].includes(e.response?.status)) setBlocked(e.response?.data?.detail || "No access");
        else toast.error(e.response?.data?.detail || "Failed to load");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [statusFilter]);

  const handleSearch = (e) => { if (e.key === "Enter") load(); };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="dp-pipeline-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold text-zinc-900">Direct Partners</h1>
          <p className="text-xs text-zinc-400 mt-0.5">{profiles.length} partner{profiles.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <DPOrderRulesDialog />
          <Button size="sm" onClick={() => navigate("/dp/apply")} data-testid="new-application-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> New Application
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="flex gap-1">
          <button onClick={() => setStatusFilter("")} className={`px-2.5 py-1 text-[11px] font-medium rounded-sm border ${!statusFilter ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50"}`} data-testid="filter-all">All</button>
          {statuses.map(s => (
            <button key={s} onClick={() => setStatusFilter(s)} className={`px-2.5 py-1 text-[11px] font-medium rounded-sm border ${statusFilter === s ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50"}`} data-testid={`filter-${s}`}>{s === "all" ? "All" : labelFor(s)}</button>
          ))}
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
          <Input placeholder="Search name, phone, GST..." className="pl-7 h-8 text-xs" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={handleSearch} data-testid="search-input" />
        </div>
      </div>

      {blocked ? <AccessBlocked detail={blocked} testid="dp-access-blocked" /> :
       loading ? <p className="text-xs text-zinc-400 py-8 text-center">Loading...</p> : profiles.length === 0 ? (
        <p className="text-center text-zinc-400 text-xs py-8 border border-dashed border-zinc-200 rounded-sm">No DP applications found.</p>
      ) : (
        <div className="border border-zinc-200 rounded-sm overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <th className="text-left px-3 py-2 font-medium text-zinc-600">Business Name</th>
                <th className="text-left px-3 py-2 font-medium text-zinc-600">Contact</th>
                <th className="text-left px-3 py-2 font-medium text-zinc-600">Phone</th>
                <th className="text-left px-3 py-2 font-medium text-zinc-600">GST</th>
                <th className="text-left px-3 py-2 font-medium text-zinc-600">Status</th>
                <th className="text-left px-3 py-2 font-medium text-zinc-600">Zone</th>
                <th className="text-left px-3 py-2 font-medium text-zinc-600">Territory</th>
                <th className="text-left px-3 py-2 font-medium text-zinc-600">OMS Customer</th>
                <th className="text-left px-3 py-2 font-medium text-zinc-600"></th>
              </tr>
            </thead>
            <tbody>
              {profiles.map(p => (
                <tr key={p._id} className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer" onClick={() => navigate(`/dp/partner/${p._id}`)} data-testid={`row-${p._id}`}>
                  <td className="px-3 py-2">
                    <span className="font-medium text-zinc-800">{p.business_name}</span>
                    {p.possible_duplicate && <Badge variant="outline" className="ml-1.5 text-[9px] border-amber-300 text-amber-600">dup?</Badge>}
                  </td>
                  <td className="px-3 py-2 text-zinc-600">{p.contact_person}</td>
                  <td className="px-3 py-2 text-zinc-600">{p.phone}</td>
                  <td className="px-3 py-2 text-zinc-500 font-mono text-[10px]">{p.gst_number || "—"}</td>
                  <td className="px-3 py-2"><Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[p.onboarding_status] || ""}`}>{labelFor(p.onboarding_status)}</Badge></td>
                  <td className="px-3 py-2 text-zinc-600">{p.territory_zone || "—"}</td>
                  <td className="px-3 py-2 text-zinc-600">{p.grant_count || p.territory_grants?.length || 0}</td>
                  <td className="px-3 py-2">
                    {p.oms_customer_id
                      ? <span className="text-emerald-600 font-medium text-[10px]">Linked</span>
                      : <span className="text-zinc-400">—</span>}
                  </td>
                  <td className="px-3 py-2"><Eye className="w-3.5 h-3.5 text-zinc-400" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
